// Το ίδιο το i18next (όχι το src/i18n): ίδιο αντικείμενο με της εφαρμογής, χωρίς το initReactI18next —
// αλλιώς κάθε test που κάνει mock το react-i18next και φτάνει εδώ (π.χ. μέσω ProductManagement) σπάει.
import i18n from 'i18next';
import { API_BASE_URL } from '../../config/api';

/**
 * Βιβλιοθήκες του καταλόγου (Φάση 3, 12/10/2026): ομάδες επιλογών, ομάδες μεγεθών, υλικά καταστήματος.
 * Ένα σημείο για τα αιτήματα: οι καρτέλες «Ομάδες επιλογών / μεγεθών / Υλικά» και το πάνελ προϊόντος
 * (νέα ομάδα / νέο υλικό επί τόπου).
 * Backend: routes/storeOptionGroups.js, storeSizeGroups.js, storeIngredients.js.
 *
 * Τα NUMERIC της PG έρχονται άλλοτε αριθμός (JSON_AGG της λίστας) κι άλλοτε κείμενο («0.50», RETURNING *):
 * εδώ γίνονται πάντα αριθμός. Το `product_count` (σε πόσα προϊόντα) το στέλνει ο server από τη Φάση 3·
 * παλιότερος server δεν το στέλνει → null, και η οθόνη δείχνει «—» αντί για ψεύτικο 0.
 */

export type SelectionType = 'single' | 'multiple';

export interface OptionChoice {
  id: number;
  group_id?: number;
  name: string;
  price_modifier: number;
  is_default: boolean;
  display_order: number;
}

export interface OptionGroup {
  id: number;
  store_id?: number;
  name: string;
  selection_type: SelectionType;
  is_required: boolean;
  display_order: number;
  choices: OptionChoice[];
  /** Σε πόσα προϊόντα είναι· null = ο server δεν το έστειλε. */
  product_count: number | null;
}

export interface SizeGroupItem {
  id: number;
  group_id?: number;
  name: string;
  /** Ε10α: η ΤΕΛΙΚΗ τιμή του μεγέθους (το όνομα της στήλης έμεινε από την εποχή της διαφοράς). */
  default_price_modifier: number;
  is_default: boolean;
  display_order: number;
}

export interface SizeGroup {
  id: number;
  store_id?: number;
  name: string;
  display_order: number;
  items: SizeGroupItem[];
}

export interface StoreIngredient {
  id: number;
  store_id?: number;
  name: string;
  extra_price: number;
  is_available: boolean;
  /** Ελεύθερο κείμενο (store_ingredients.category, VARCHAR 100) — όχι κλειδί πίνακα. */
  category: string | null;
  display_order: number;
  product_count: number | null;
}

/** Κατηγορία προϊόντων (επίπεδη λίστα) — για το «μπαίνει αυτόματα στα νέα προϊόντα». */
export interface LibraryCategory {
  id: number;
  name: string;
  parent_id: number | null;
  parent_name: string | null;
  color: string | null;
  display_order: number;
  is_hidden: boolean;
}

export interface NewOptionGroupInput {
  name: string;
  selection_type: SelectionType;
  is_required: boolean;
  choices: Array<{ name: string; price_modifier: number; is_default?: boolean }>;
}

export interface NewSizeGroupInput {
  name: string;
  /** price_modifier = η τελική τιμή του μεγέθους (γράφεται ως default_price_modifier). */
  items: Array<{ name: string; price_modifier: number; is_default?: boolean }>;
}

export interface NewIngredientInput {
  name: string;
  /** Τιμή έξτρα σε € (κενό = 0). */
  price?: number;
  /** Κατηγορία υλικού ως κείμενο (π.χ. «Τυριά»)· τα υλικά δεν έχουν πίνακα κατηγοριών. */
  category?: string | null;
  is_available?: boolean;
}

/* ───────────────────────────── αιτήματα ───────────────────────────── */

const GREEK = /[Ͱ-Ͽἀ-῿]/;

/** Ό,τι διαβάζει ο χειριστής: ο server γράφει άλλα ελληνικά (409, 400 με code) κι άλλα αγγλικά/τεχνικά. */
function userMessage(status: number, code: string | null, serverMessage: string | null, reading: boolean): string {
  if (code === 'CATALOG_CENTRAL') return i18n.t('products:libraries.errors.central');
  if (code === 'NETWORK') return i18n.t('products:libraries.errors.network');
  if (serverMessage && GREEK.test(serverMessage)) return serverMessage;
  if (status === 409 || (status === 400 && /already exists/i.test(serverMessage || ''))) return i18n.t('products:libraries.errors.duplicate');
  if (status === 403) return i18n.t('products:libraries.errors.forbidden');
  if (status === 404) return i18n.t('products:libraries.errors.notFound');
  return i18n.t(reading ? 'products:libraries.loadError' : 'products:libraries.errors.generic');
}

/**
 * Αποτυχία. `message` = έτοιμο ελληνικό μήνυμα για τον χειριστή. status 0 = δεν έφτασε στον server
 * (code NETWORK) ή άκυρα δεδομένα πριν από κάθε αίτημα (code INVALID_INPUT).
 * code CATALOG_CENTRAL = κεντρικός κατάλογος, μόνο ανάγνωση.
 */
export class LibraryApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  /** Το μήνυμα του server (message ή error), όπως ήρθε — για διάγνωση. */
  readonly serverMessage: string | null;

  /** `reading`: αποτυχία ανάγνωσης (GET) — άλλο γενικό μήνυμα από την αποθήκευση. */
  constructor(status: number, body: unknown, reading = false) {
    const b = (body && typeof body === 'object' ? body : {}) as { error?: unknown; message?: unknown; code?: unknown };
    const code = typeof b.code === 'string' ? b.code : null;
    // Το 403 του κλειδώματος έχει τα ελληνικά στο message· τα 400/409 στο error.
    const serverMessage = typeof b.error === 'string' && GREEK.test(b.error) ? b.error
      : typeof b.message === 'string' ? b.message
        : typeof b.error === 'string' ? b.error : null;
    super(code === 'INVALID_INPUT' && serverMessage ? serverMessage : userMessage(status, code, serverMessage, reading));
    this.name = 'LibraryApiError';
    this.status = status;
    this.code = code;
    this.serverMessage = serverMessage;
  }
}

export const isCentralLockError = (err: unknown): boolean =>
  err instanceof LibraryApiError && err.code === 'CATALOG_CENTRAL';

const invalid = (message: string) => new LibraryApiError(0, { code: 'INVALID_INPUT', error: message });

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('xentrix_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';

async function call<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const url = `${API_BASE_URL}/api${path}`;
  let res: Response;
  try {
    if (method === 'GET') {
      res = await fetch(url, { headers: authHeaders() });
    } else if (body === undefined) {
      res = await fetch(url, { method, headers: authHeaders() });
    } else {
      res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
    }
  } catch {
    throw new LibraryApiError(0, { code: 'NETWORK' });
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new LibraryApiError(res.status, data, method === 'GET');
  return data as T;
}

/* ───────────────────────────── κανονικοποίηση ───────────────────────────── */

const money = (v: unknown): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};
const int = (v: unknown): number => {
  const n = typeof v === 'number' ? v : parseInt(String(v ?? ''), 10);
  return Number.isFinite(n) ? n : 0;
};
const productCount = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
/** Δύο δεκαδικά, όπως το NUMERIC(10,2) της βάσης. */
export const roundMoney = (v: number): number => Math.round(v * 100) / 100;

type Raw = Record<string, any>;

export const normalizeChoice = (raw: Raw): OptionChoice => ({
  ...raw,
  id: int(raw.id),
  name: String(raw.name ?? ''),
  price_modifier: money(raw.price_modifier),
  is_default: raw.is_default === true,
  display_order: int(raw.display_order),
});

export const normalizeOptionGroup = (raw: Raw): OptionGroup => ({
  ...raw,
  id: int(raw.id),
  name: String(raw.name ?? ''),
  selection_type: raw.selection_type === 'multiple' ? 'multiple' : 'single',
  is_required: raw.is_required === true,
  display_order: int(raw.display_order),
  choices: Array.isArray(raw.choices) ? raw.choices.map(normalizeChoice) : [],
  product_count: productCount(raw.product_count),
});

export const normalizeSizeItem = (raw: Raw): SizeGroupItem => ({
  ...raw,
  id: int(raw.id),
  name: String(raw.name ?? ''),
  default_price_modifier: money(raw.default_price_modifier),
  is_default: raw.is_default === true,
  display_order: int(raw.display_order),
});

export const normalizeSizeGroup = (raw: Raw): SizeGroup => ({
  ...raw,
  id: int(raw.id),
  name: String(raw.name ?? ''),
  display_order: int(raw.display_order),
  items: Array.isArray(raw.items) ? raw.items.map(normalizeSizeItem) : [],
});

export const normalizeIngredient = (raw: Raw): StoreIngredient => ({
  ...raw,
  id: int(raw.id),
  name: String(raw.name ?? ''),
  extra_price: money(raw.extra_price),
  is_available: raw.is_available !== false,
  category: typeof raw.category === 'string' && raw.category.trim() ? raw.category : null,
  display_order: int(raw.display_order),
  product_count: productCount(raw.product_count),
});

const normalizeCategory = (raw: Raw): LibraryCategory => ({
  id: int(raw.id),
  name: String(raw.name ?? ''),
  parent_id: raw.parent_id == null ? null : int(raw.parent_id),
  parent_name: typeof raw.parent_name === 'string' ? raw.parent_name : null,
  color: typeof raw.color === 'string' ? raw.color : null,
  display_order: int(raw.display_order),
  is_hidden: raw.is_hidden === true,
});

const asList = (data: unknown): Raw[] => (Array.isArray(data) ? (data as Raw[]) : []);

/* ───────────────────────────── έλεγχοι εισόδου ───────────────────────────── */

/** `row`: γραμμή μέσα σε ομάδα (επιλογή / μέγεθος) — άλλο μήνυμα από το όνομα της ίδιας της ομάδας. */
const cleanName = (name: unknown, row = false): string => {
  const v = typeof name === 'string' ? name.trim() : '';
  if (!v) throw invalid(i18n.t(row ? 'products:libraries.validation.rowNameRequired' : 'products:libraries.validation.nameRequired'));
  return v;
};

const cleanPrice = (price: unknown, allowNegative: boolean): number => {
  const v = typeof price === 'number' ? price : NaN;
  if (!Number.isFinite(v)) throw invalid(i18n.t('products:libraries.validation.priceInvalid'));
  if (!allowNegative && v < 0) throw invalid(i18n.t('products:libraries.validation.priceNegative'));
  return roundMoney(v);
};

/** Ονόματα μοναδικά μέσα στην ομάδα (UNIQUE(group_id, name) στη βάση). */
const assertUniqueNames = (names: string[]) => {
  const seen = new Set<string>();
  names.forEach(n => {
    if (seen.has(n)) throw invalid(i18n.t('products:libraries.validation.rowNameDuplicate', { name: n }));
    seen.add(n);
  });
};

/* ───────────────────────────── ομάδες επιλογών ───────────────────────────── */

export async function listOptionGroups(storeId: number): Promise<OptionGroup[]> {
  return asList(await call('GET', `/stores/${storeId}/option-groups`)).map(normalizeOptionGroup);
}

/**
 * Νέα ομάδα με τις επιλογές της (POST ομάδα, μετά POST κάθε επιλογή με τη σειρά της). Αν αποτύχει
 * κάποια επιλογή, η μισή ομάδα σβήνεται (καμία σύνδεση δεν έχει ακόμη) και πετιέται το σφάλμα.
 * Σε «Μία» μένει μόνο η πρώτη προεπιλογή — ο server κρατά έτσι κι αλλιώς μία.
 */
export async function createOptionGroup(storeId: number, input: NewOptionGroupInput): Promise<OptionGroup> {
  const name = cleanName(input?.name);
  const selection_type: SelectionType = input?.selection_type === 'multiple' ? 'multiple' : 'single';
  let defaultTaken = false;
  const choices = (input?.choices || []).map(c => {
    const wantsDefault = c?.is_default === true && !(selection_type === 'single' && defaultTaken);
    if (wantsDefault) defaultTaken = true;
    return {
      name: cleanName(c?.name, true),
      price_modifier: cleanPrice(c?.price_modifier, true),
      is_default: wantsDefault,
    };
  });
  assertUniqueNames(choices.map(c => c.name));

  const group = await call<Raw>('POST', `/stores/${storeId}/option-groups`, {
    name, selection_type, is_required: input?.is_required === true,
  });
  const created: Raw[] = [];
  try {
    for (let i = 0; i < choices.length; i++) {
      created.push(await call<Raw>('POST', `/option-groups/${group.id}/choices`, { ...choices[i], display_order: i }));
    }
  } catch (err) {
    await call('DELETE', `/option-groups/${group.id}`).catch(() => undefined);
    throw err;
  }
  return normalizeOptionGroup({ ...group, choices: created, product_count: 0 });
}

export interface OptionGroupPatch { name?: string; selection_type?: SelectionType; is_required?: boolean; display_order?: number }
export interface OptionChoiceBody { name: string; price_modifier: number; is_default: boolean; display_order: number }

/** Αλλάζει μόνο τα πεδία που στέλνονται (COALESCE στον server). */
export async function updateOptionGroup(groupId: number, patch: OptionGroupPatch): Promise<void> {
  await call('PUT', `/option-groups/${groupId}`, patch);
}

/** Σβήνει την ομάδα, τις επιλογές της και τις συνδέσεις της με προϊόντα (CASCADE). */
export async function deleteOptionGroup(groupId: number): Promise<void> {
  await call('DELETE', `/option-groups/${groupId}`);
}

export async function createOptionChoice(groupId: number, body: OptionChoiceBody): Promise<OptionChoice> {
  return normalizeChoice(await call<Raw>('POST', `/option-groups/${groupId}/choices`, body));
}

export async function updateOptionChoice(choiceId: number, patch: Partial<OptionChoiceBody>): Promise<void> {
  await call('PUT', `/option-groups/choices/${choiceId}`, patch);
}

export async function deleteOptionChoice(choiceId: number): Promise<void> {
  await call('DELETE', `/option-groups/choices/${choiceId}`);
}

/* ───────────────── κατηγορίες: «μπαίνει αυτόματα στα νέα προϊόντα» ───────────────── */

export async function listCategories(storeId: number): Promise<LibraryCategory[]> {
  const data = await call<{ categories?: Raw[] } | Raw[]>('GET', `/products/stores/${storeId}/categories?flat=true`);
  const rows = Array.isArray(data) ? data : asList(data?.categories);
  return rows.map(normalizeCategory);
}

/** Οι ομάδες που μπαίνουν σε κάθε ΝΕΟ προϊόν της κατηγορίας, με τη σειρά τους. */
export async function getCategoryDefaultGroupIds(categoryId: number): Promise<number[]> {
  return asList(await call('GET', `/categories/${categoryId}/default-option-groups`)).map(r => int(r.id));
}

/** Αντικαθιστά ΟΛΟ το σύνολο της κατηγορίας (ο server σβήνει και ξαναγράφει). */
export async function setCategoryDefaultGroupIds(categoryId: number, groupIds: number[]): Promise<void> {
  await call('PUT', `/categories/${categoryId}/default-option-groups`, { option_group_ids: groupIds });
}

/* ───────────────────────────── ομάδες μεγεθών ───────────────────────────── */

export async function listSizeGroups(storeId: number): Promise<SizeGroup[]> {
  return asList(await call('GET', `/stores/${storeId}/size-groups`)).map(normalizeSizeGroup);
}

/**
 * Νέα ομάδα μεγεθών με τα μέλη της. Η τιμή κάθε μέλους είναι η τελική τιμή του μεγέθους (Ε10α) και
 * δεν μπορεί να είναι αρνητική (Ε13α — ο server απορρίπτει νέα αρνητική). Αποτυχία μέλους → η μισή
 * ομάδα σβήνεται.
 */
export async function createSizeGroup(storeId: number, input: NewSizeGroupInput): Promise<SizeGroup> {
  const name = cleanName(input?.name);
  let defaultTaken = false;
  const items = (input?.items || []).map(item => {
    const wantsDefault = item?.is_default === true && !defaultTaken;
    if (wantsDefault) defaultTaken = true;
    return {
      name: cleanName(item?.name, true),
      default_price_modifier: cleanPrice(item?.price_modifier, false),
      is_default: wantsDefault,
    };
  });
  assertUniqueNames(items.map(i => i.name));

  const group = await call<Raw>('POST', `/stores/${storeId}/size-groups`, { name });
  const created: Raw[] = [];
  try {
    for (let i = 0; i < items.length; i++) {
      created.push(await call<Raw>('POST', `/size-groups/${group.id}/items`, { ...items[i], display_order: i }));
    }
  } catch (err) {
    await call('DELETE', `/size-groups/${group.id}`).catch(() => undefined);
    throw err;
  }
  return normalizeSizeGroup({ ...group, items: created });
}

export interface SizeItemBody { name: string; default_price_modifier: number; is_default: boolean; display_order: number }

export async function updateSizeGroup(groupId: number, patch: { name?: string; display_order?: number }): Promise<void> {
  await call('PUT', `/size-groups/${groupId}`, patch);
}

export async function deleteSizeGroup(groupId: number): Promise<void> {
  await call('DELETE', `/size-groups/${groupId}`);
}

export async function createSizeItem(groupId: number, body: SizeItemBody): Promise<SizeGroupItem> {
  return normalizeSizeItem(await call<Raw>('POST', `/size-groups/${groupId}/items`, body));
}

export async function updateSizeItem(itemId: number, patch: Partial<SizeItemBody>): Promise<void> {
  await call('PUT', `/size-groups/items/${itemId}`, patch);
}

export async function deleteSizeItem(itemId: number): Promise<void> {
  await call('DELETE', `/size-groups/items/${itemId}`);
}

/**
 * Ε10α (P5-TEMPLATES): η τιμή μέλους ομάδας είναι η ΤΕΛΙΚΗ τιμή του μεγέθους, όχι διαφορά από βάση —
 * η εισαγωγή ομάδας στη φόρμα προϊόντος τη γράφει αυτούσια ως price_modifier. Η ομάδα ΑΝΤΙΓΡΑΦΕΤΑΙ:
 * μετά την εισαγωγή το προϊόν δεν «ξέρει» την ομάδα.
 */
export function sizeGroupToProductSizes(
  items: Array<Pick<SizeGroupItem, 'name' | 'is_default'> & { default_price_modifier: number | string }> | undefined,
): Array<{ name: string; price_modifier: number; is_default: boolean }> {
  return (items || []).map(item => ({
    name: item.name,
    price_modifier: Number(item.default_price_modifier) || 0,
    is_default: item.is_default === true,
  }));
}

/** Απόλυτη τιμή, χωρίς «+»: «1.90€» (κλασική μορφή, όπως η φόρμα προϊόντος). */
export const formatSizePrice = (v: number | string | null | undefined) => `${(Number(v) || 0).toFixed(2)}€`;

/* ───────────────────────────── υλικά ───────────────────────────── */

export async function listIngredients(storeId: number): Promise<StoreIngredient[]> {
  return asList(await call('GET', `/stores/${storeId}/store-ingredients`)).map(normalizeIngredient);
}

export async function createIngredient(storeId: number, input: NewIngredientInput): Promise<StoreIngredient> {
  const name = cleanName(input?.name);
  const extra_price = input?.price == null ? 0 : cleanPrice(input.price, false);
  const category = typeof input?.category === 'string' && input.category.trim() ? input.category.trim() : null;
  const row = await call<Raw>('POST', `/stores/${storeId}/store-ingredients`, {
    name, extra_price, category, is_available: input?.is_available !== false,
  });
  return normalizeIngredient({ ...row, product_count: 0 });
}

export interface IngredientBody { name: string; extra_price: number; is_available: boolean; category: string | null; display_order: number }

/**
 * Ο server ξαναγράφει ΟΛΑ τα πεδία (και display_order → 0 όταν λείπει): στέλνε πάντα ολόκληρη τη
 * γραμμή, με τη σειρά που είχε.
 */
export async function updateIngredient(ingredientId: number, body: IngredientBody): Promise<StoreIngredient> {
  return normalizeIngredient(await call<Raw>('PUT', `/store-ingredients/${ingredientId}`, body));
}

/** Σβήνει το υλικό και τις συνδέσεις του με προϊόντα (CASCADE). */
export async function deleteIngredient(ingredientId: number): Promise<void> {
  await call('DELETE', `/store-ingredients/${ingredientId}`);
}
